'use strict'

import Color from './Color'

// Defaults
const MAX_NUMBER_OF_COLORS = 5
const DEFAULT_WORKING_COLOR_POS = 0

/**
 * ColorSample
 * Manages color slots and the currently active color position.
 */
class ColorSample {
    constructor() {
        this.colors = new Map()
        this.workingColorPos = DEFAULT_WORKING_COLOR_POS

        // UI hooks – wired from outside (CanvasState)
        this.onColorUIRender = null // called when ONE color changes
        this.onColorListChanged = null // called when the LIST changes
    }

    static get MAX_NUMBER_OF_COLORS() {
        return MAX_NUMBER_OF_COLORS
    }
    static get DEFAULT_WORKING_COLOR_POS() {
        return DEFAULT_WORKING_COLOR_POS
    }

    toJSON() {
        const mapToObject = (map) => {
            const obj = {}
            for (const [k, v] of map) {
                obj[k] = v instanceof Map ? mapToObject(v) : v
            }
            return obj
        }
        return {
            colors: mapToObject(this.colors),
            workingColorPos: this.workingColorPos,
        }
    }

    /**
     * Store color at a given index and keep dropcolor_id in sync.
     * @param {number} colorNumber
     * @param {Color} color
     */
    setColorByNumber(colorNumber, color) {
        const n = parseInt(colorNumber, 10)
        color.dropcolor_id = n
        if (typeof color?.applyDefaultNameIfEmpty === 'function') {
            color.applyDefaultNameIfEmpty(n)
        }
        this.colors.set(n, color)
    }

    setTextureAtPosition(position, texture) {
        const c = this.colors.get(position)
        if (c) c.texture_data = texture
    }

    getColorByNumber(position) {
        return this.colors.has(position) ? this.colors.get(position) : undefined
    }

    getCurrentColor() {
        return this.getColorByNumber(this.workingColorPos)
    }

    /**
     * Update current slot’s color; UI is delegated to onColorUIRender.
     */
    setCurrentColor(color) {
        console.log('setCurrentColor: ', this.workingColorPos)
        console.log('typeof onColorUIRender in setCurrentColor =', typeof this.onColorUIRender)
        console.log('onColorUIRender value =', this.onColorUIRender)

        if (!this.workingColorPos) return

        this.setColorByNumber(this.workingColorPos, color)

        if (typeof this.onColorUIRender === 'function') {
            this.onColorUIRender(color)
        }
    }

    /**
     * Delete the color at `colorNumber` and shift subsequent ones down.
     */
    deleteColorNumber(colorNumber) {
        const nbOfColorBeforeDelete = this.numberOfColors()

        if (colorNumber < nbOfColorBeforeDelete || nbOfColorBeforeDelete === 1) {
            for (let i = colorNumber + 1; i <= nbOfColorBeforeDelete; i++) {
                this.setColorByNumber(i - 1, this.getColorByNumber(i))
            }
        }

        // remove last element
        this.colors.delete(nbOfColorBeforeDelete)

        if (this.workingColorPos === nbOfColorBeforeDelete) {
            this.workingColorPos = Math.max(1, this.workingColorPos - 1)
        }

        this.updateColorInfos()
    }

    /**
     * Add a new swatchColor as a new color at the end.
     * @param {SwatchColor} swatchColor
     */
    addSwatchColor(swatchColor) {
        const nextIndex = this.numberOfColors() + 1
        let color = this.getColorByNumber(nextIndex)

        if (!color) {
            color = {}
            color.bleach_value = Color.DEFAULT_BLEACH_VALUE
            color.texture_scale = Color.DEFAULT_TEXTURE_SCALE
            if (swatchColor.textureApplied != null) {
                color.texture = swatchColor.textureApplied
            }
        }

        color.swatchColor = swatchColor
        this.setColorByNumber(nextIndex, color)
        this.updateColorInfos()
    }

    /**
     * Move color from position A to B (re-indexing in-between).
     */
    moveColorNumber(colorToMoveNumber, colorNumber) {
        const colorToMove = this.getColorByNumber(colorToMoveNumber)
        if (!colorToMove) return

        const toColorNumber = Math.min(this.numberOfColors(), colorNumber)

        if (toColorNumber < colorToMoveNumber) {
            for (let i = colorToMoveNumber - 1; i >= toColorNumber; i--) {
                this.setColorByNumber(i + 1, this.getColorByNumber(i))
            }
        } else {
            for (let i = colorToMoveNumber + 1; i <= toColorNumber; i++) {
                this.setColorByNumber(i - 1, this.getColorByNumber(i))
            }
        }

        this.setColorByNumber(toColorNumber, colorToMove)
        this.workingColorPos = toColorNumber

        this.updateColorInfos()
    }

    numberOfColors() {
        return this.colors.size
    }

    /**
     * “Model only” version of the legacy updateColorInfos.
     *
     * It:
     *   1. Clamps workingColorPos.
     *   2. Notifies onColorListChanged(colors) if provided.
     *
     * All DOM / Dropcolor work is done by whoever wires onColorListChanged.
     */
    updateColorInfos() {
        // clamp workingColorPos
        if (this.numberOfColors() === 0) {
            this.workingColorPos = 0
        } else if (this.workingColorPos > this.numberOfColors()) {
            this.workingColorPos = Math.max(1, this.workingColorPos - 1)
        }

        if (typeof this.onColorListChanged === 'function') {
            this.onColorListChanged(this.colors)
        }
    }
}

export default ColorSample
